import { cn, isLinkActive } from '@sohanemon/utils';
import { Iconify, Media } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import { formatNumber } from '@sohanscript/lib/utils/format';
import { TextHoverEffect } from '@sohanscript/ui/animated/text-hover';
import { ClientOnly, useLocation } from '@tanstack/react-router';
import { Hydrate } from '@tanstack/react-start';
import { idle } from '@tanstack/react-start/hydration';
import { type ComponentProps, Suspense } from 'react';
import { useSohanEmon } from '#/lib/hooks/sohanemon.tsx';
import { getAppEnv, getBrand } from '#/lib/utils/app-env.ts';
import { Link } from '../link';
import { Socials } from '../socials';
import { Brand } from './brand';
import { QuickLinks } from './footer-quick-links';

type FooterProps = ComponentProps<'footer'>;

const noContact = getAppEnv('no-contact');

export function Footer({ className, ...props }: FooterProps) {
	const location = useLocation();
	const currentPath = location.pathname;
	const os = isLinkActive({ targetPath: '/os', currentPath });
	const nav = useSohanEmon().links;

	if (os) return null;

	const year = new Date().getFullYear();

	return (
		<footer
			className={cn(
				'relative isolate mt-(--height-footer-top) min-h-footer overflow-hidden border-border/60 border-t',
				className,
			)}
			{...props}
		>
			<div className="container relative mx-auto px-6 pt-20">
				{/* Main grid */}
				<div className="grid gap-12 lg:grid-cols-12">
					{/* Brand */}
					<div className={cn('lg:col-span-6', noContact && 'lg:col-span-8')}>
						<Brand full />
						<p className="mt-5 max-w-sm text-muted-foreground text-sm leading-relaxed">
							{m.footer_tagline()}
						</p>
						<div className="mt-6 inline-flex items-center gap-2.5 rounded-full border border-border/40 bg-muted/30 px-3.5 py-1.5 font-mono text-2xs tracking-wide">
							<span className="relative flex size-1.5">
								<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
								<span className="relative inline-flex size-1.5 rounded-full bg-primary" />
							</span>
							<span className="text-muted-foreground">sys</span>
							<span>·</span>
							<span className="text-muted-foreground">
								{m.footer_open_for_work()}
							</span>
							<span>·</span>
							<span className="text-primary/80">ping &lt;1ms</span>
						</div>
					</div>

					{/* Navigate */}
					<div className="lg:col-span-2">
						<h2 className="mb-4 font-mono text-muted-foreground text-xs uppercase tracking-[0.18em]">
							~/ {m.footer_navigate()}
						</h2>
						<ul className="space-y-2.5">
							{nav.map((n) => (
								<li key={n.href}>
									<Link
										to={n.href as '/'}
										className="group inline-flex items-center gap-2 text-foreground/80 text-sm transition-colors hover:text-foreground"
									>
										<Iconify
											icon="material-symbols:prompt-suggestion"
											className="size-3.5 text-muted-foreground/50 transition-colors group-hover:text-primary"
										/>

										<span className="relative">
											{n.label}
											<span className="absolute start-0 -bottom-0.5 h-px w-0 bg-foreground transition-[width] duration-300 group-hover:w-full" />
										</span>
									</Link>
								</li>
							))}
						</ul>
					</div>

					{/* Quick Links */}
					<div className="lg:col-span-2">
						<h2 className="mb-4 font-mono text-muted-foreground text-xs uppercase tracking-[0.18em]">
							~/ {m.footer_quick_links()}
						</h2>
						<Suspense fallback={null}>
							<QuickLinks />
						</Suspense>
					</div>

					{/* Connect */}
					{noContact ? null : (
						<div className="lg:col-span-2">
							<h2 className="mb-4 font-mono text-muted-foreground text-xs uppercase tracking-[0.18em]">
								~/ {m.footer_connect()}
							</h2>
							<Socials
								size="sm"
								withLabel
								className="flex-col items-start gap-5 text-xs"
							/>
						</div>
					)}
				</div>

				<Hydrate when={idle()}>
					<Media breakpoint="sm">
						<TextHoverEffect
							className="relative z-10 -mb-30 font-matrix text-[2.4rem]"
							text={getBrand().domain}
						/>
					</Media>
				</Hydrate>

				{/* Bottom bar */}
				<div className="mt-16 flex flex-col items-start justify-between gap-4 border-border/60 border-t py-6 backdrop-blur md:flex-row md:items-center">
					<p className="font-mono text-muted-foreground text-xs">
						<Iconify
							icon="lucide:copyright"
							className="inline-block size-3 align-top"
						/>{' '}
						<ClientOnly>
							{formatNumber(new Date().getFullYear(), {
								useGrouping: false,
							})}
						</ClientOnly>{' '}
						{getBrand().domain} — {m.footer_all_systems_operational()}
						<span className="ml-1 inline-block h-3 w-1.5 translate-y-0.5 animate-pulse bg-foreground" />
					</p>
					<div className="flex items-center gap-5 font-mono text-muted-foreground text-xs">
						<span className="inline-flex items-center gap-1.5">
							<Iconify icon="lucide:git-branch" className="size-3" />
							main
						</span>
						<span className="inline-flex items-center gap-1.5">
							<Iconify icon="lucide:zap" className="size-3 text-primary" />v
							{formatNumber(year, { useGrouping: false })}.1
						</span>
					</div>
				</div>
			</div>
		</footer>
	);
}
